import { useRef, useState } from 'react';
import { formatHitFactor, formatMs } from '@flm9/i18n/labels';
import { roundHitFactor, type ScoringMode } from '@flm9/scoring';
import { queueShot, correctQueuedShot, type CachedShot } from '@flm9/sync';
import { track } from '@flm9/telemetry-client';
import { wakeRefereeSync } from '../lib/sync';
import {
  HitIndicator,
  type HitSchemaKind,
  type HitZone,
  type PendingDetection,
  type PenaltyEntry,
  type ShotPosition,
  type TargetZone,
} from '@flm9/stream-client';
import { useTimerRun } from '../lib/timer/provider';
import { ShotUndoToast } from './ShotUndoToast';
import { formatPenaltyPoints } from '../lib/penalties';

interface Props {
  assignmentId: string;
  shotCount: number;
  shooterLabel: string;
  shots: CachedShot[];
  values: number[];
  schemaKind: HitSchemaKind;
  schemaZones: HitZone[];
  /** Geometric zone definitions for shape-faithful rendering. */
  targetZones?:
    | Array<{
        zoneCode: string;
        ordering: number;
        shape: 'circle' | 'rect' | 'polygon';
        geometry: unknown;
        displayColor: string | null;
      }>
    | undefined;
  shotPositions?: ShotPosition[] | undefined;
  pendingDetections?: PendingDetection[] | undefined;
  penalties?: PenaltyEntry[] | undefined;
  onConfirmDetection?: ((detectionId: string) => void) | undefined;
  onDismissDetection?: ((detectionId: string) => void) | undefined;
  onShot: () => void;
  /** When true, suppress the inline hit indicator — the parent renders
   *  it as a floating PiP overlay instead, so showing it inline would
   *  be a duplicate. */
  hideHitIndicator?: boolean;
  /** Current PiP-mode state, used to label the toggle button. When
   *  `onToggleHitPip` is provided, a small always-visible toggle is
   *  rendered in the header so the referee can pop the indicator out
   *  before the first shot lands. */
  hitPipActive?: boolean;
  onToggleHitPip?: () => void;
  /** Camera PiP toggle — sibling pill next to the hit-PiP toggle. Same
   *  rationale: discoverable before any shot, and decoupled from the
   *  inline indicator so it stays reachable when the indicator is
   *  hidden in PiP mode. */
  streamPipActive?: boolean;
  onToggleStreamPip?: () => void;
  /** Idempotency keys of shots still in the outbox (REF-9: amber dot). */
  unsentKeys?: ReadonlySet<string>;
  /**
   * Sum of the shooter's penalties incl. queued ones (`penaltyTotal`, ≤ 0).
   * Added to the points shown here (and the HF preview).
   */
  penaltyPoints?: number;
  /** Round total time (TimingPad / timer) for the HF preview. */
  elapsedTimeMs?: number | null;
  /**
   * The SCORING MODE decides whether there is a hit factor (engine rule,
   * `roundHitFactor`): speed only — a ring round in speed mode has one.
   */
  scoringMode: ScoringMode;
  /** A DQ'd round has no hit factor (server: leaderboard.ts sets it null). */
  disqualified?: boolean;
}

export function NumericKeypad({
  assignmentId,
  shotCount,
  shooterLabel,
  shots,
  values,
  schemaKind,
  schemaZones,
  targetZones,
  shotPositions,
  pendingDetections,
  penalties,
  onConfirmDetection,
  onDismissDetection,
  onShot,
  hideHitIndicator,
  hitPipActive,
  onToggleHitPip,
  streamPipActive,
  onToggleStreamPip,
  unsentKeys,
  penaltyPoints = 0,
  elapsedTimeMs = null,
  scoringMode,
  disqualified = false,
}: Props) {
  const [editing, setEditing] = useState<CachedShot | null>(null);
  const [busy, setBusy] = useState(false);
  // Last deleted shot, offered for undo (REF-21).
  const [deleted, setDeleted] = useState<CachedShot | null>(null);
  // Scoped to this shooter: a previous shooter's stopped run must not
  // lend its shot times to this one.
  const {
    source: timerSource,
    status: timerStatus,
    isConnected: timerConnected,
    ownsRun: timerRunIsOurs,
  } = useTimerRun(assignmentId);
  // BLE-detected shots beyond the ones the referee has already scored.
  // Rendered as ghost cells in the shot grid so the referee can see
  // how many shots they still owe a score for — when OpenCV doesn't
  // catch a hit, the timer still does, and tapping a value here will
  // pair with the next pending BLE shot's tFromStartMs.
  const pendingBleShots =
    timerConnected && timerStatus.shots.length > shots.length
      ? timerStatus.shots.slice(shots.length, shotCount)
      : [];

  const recording = useRef(false);
  async function recordShot(value: number) {
    if (recording.current) return;
    recording.current = true;
    // Flight recorder: what the referee entered → which shot got queued
    // (the upload itself is tracked by the sync drainer).
    const intent = track('Schuss erfassen', { assignmentId, value });
    try {
      const queued = await queueShot({
        assignmentId,
        shotCount,
        fields: (ord) => {
          const bleShot =
            timerConnected && timerRunIsOurs
              ? timerSource.status().shots.find((s) => s.ord === ord)
              : undefined;
          return {
            valueNumeric: value,
            zoneCode: null,
            firstShotTimeMs: bleShot?.tFromStartMs ?? null,
            elapsedTimeMs: null,
            ...(bleShot ? { source: 'ble_timer' as const } : {}),
          };
        },
      });
      if (queued)
        intent.ok({ ord: queued.ord, clientKey: queued.clientIdempotencyKey, queued: true });
      else intent.fail('not queued (stage already full)');
      wakeRefereeSync();
      onShot();
    } catch (err) {
      intent.fail(err);
      console.error('recordShot failed', err);
      alert('Schuss konnte nicht gespeichert werden. Bitte erneut versuchen.');
    } finally {
      recording.current = false;
    }
  }

  async function applyEdit(value: number) {
    if (!editing) return;
    setBusy(true);
    try {
      await correctQueuedShot(editing.roundAssignmentId, editing.clientIdempotencyKey, {
        valueNumeric: value,
      });
      wakeRefereeSync();
      onShot();
      setEditing(null);
    } catch (err) {
      console.error('updateShot failed', err);
      alert('Korrektur fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  }

  async function applyDelete() {
    if (!editing) return;
    setBusy(true);
    try {
      await correctQueuedShot(editing.roundAssignmentId, editing.clientIdempotencyKey, null);
      wakeRefereeSync();
      onShot();
      setDeleted(editing);
      setEditing(null);
    } catch (err) {
      console.error('removeShot failed', err);
      alert('Löschen fehlgeschlagen — bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  }

  const sum = shots.reduce((acc, s) => acc + (s.valueNumeric ?? 0), 0) + penaltyPoints;
  const hf = disqualified ? null : roundHitFactor(scoringMode, sum, elapsedTimeMs);
  const done = shots.length >= shotCount;
  const cols = values.length <= 4 ? 'grid-cols-4' : 'grid-cols-6';

  return (
    <section>
      <div className="mb-4 flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">am Schießstand</p>
          <h2 className="truncate text-3xl font-extrabold leading-tight sm:text-4xl">
            {shooterLabel}
          </h2>
          <p className="mt-1 text-sm text-zinc-400">
            {shots.length} / {shotCount} Schüsse
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Punkte</p>
          <div className="text-5xl font-bold tabular-nums">{sum}</div>
          {penaltyPoints !== 0 && (
            <div className="text-xs tabular-nums text-orange-300">
              inkl. Strafen {formatPenaltyPoints(penaltyPoints)}
            </div>
          )}
          {hf != null && (
            <div className="text-xs text-emerald-400 tabular-nums">HF {formatHitFactor(hf)}</div>
          )}
        </div>
      </div>

      {(onToggleHitPip || onToggleStreamPip) && (
        <div className="mb-3 flex flex-wrap justify-end gap-2">
          {onToggleHitPip && (
            <button
              onClick={onToggleHitPip}
              title={
                hitPipActive
                  ? 'Trefferanzeige inline anzeigen'
                  : 'Trefferanzeige als Bild-im-Bild ausklappen'
              }
              aria-pressed={hitPipActive ? 'true' : 'false'}
              className={`min-h-12 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                hitPipActive
                  ? 'border-emerald-500 bg-emerald-700 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              {hitPipActive ? '🪟 PiP an' : '🪟 Trefferanzeige als PiP'}
            </button>
          )}
          {onToggleStreamPip && (
            <button
              onClick={onToggleStreamPip}
              title={
                streamPipActive
                  ? 'Kamera-PiP schließen'
                  : 'Live-Kamera als Bild-im-Bild — weckt die CV-Erkennung'
              }
              aria-pressed={streamPipActive ? 'true' : 'false'}
              className={`min-h-12 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                streamPipActive
                  ? 'border-amber-400 bg-amber-600 text-white'
                  : 'border-zinc-700 bg-zinc-900 text-zinc-300 hover:border-zinc-500'
              }`}
            >
              {streamPipActive ? '📹 Kamera an' : '📹 Kamera als PiP'}
            </button>
          )}
        </div>
      )}

      <div className={`mb-6 grid ${cols} gap-2`}>
        {values.map((v) => (
          <button
            key={v}
            onClick={() => void recordShot(v)}
            disabled={done}
            className="aspect-square rounded-lg border-2 border-zinc-700 text-3xl font-bold tabular-nums hover:border-emerald-500 active:bg-emerald-700 disabled:opacity-30"
          >
            {v}
          </button>
        ))}
      </div>

      {shots.length > 0 && !hideHitIndicator && (
        <div className="mb-4">
          <HitIndicator
            shots={shots}
            shotCount={shotCount}
            schemaKind={schemaKind}
            zones={schemaZones}
            targetZones={targetZones as TargetZone[] | undefined}
            shotPositions={shotPositions}
            pendingDetections={pendingDetections}
            penalties={penalties}
            onConfirmDetection={onConfirmDetection}
            onDismissDetection={onDismissDetection}
          />
        </div>
      )}

      <p className="mb-1 text-xs text-zinc-500">
        Tipp: Schuss antippen, um Wert zu korrigieren oder zu löschen.
        {pendingBleShots.length > 0 && (
          <>
            {' '}
            <span className="text-amber-400">
              {pendingBleShots.length} vom Timer erkannt — Wertung oben wählen.
            </span>
          </>
        )}
      </p>
      <ul className="grid grid-cols-5 gap-2">
        {shots.map((s) => (
          <li key={s.id}>
            <button
              onClick={() => setEditing(s)}
              className="relative block w-full rounded border border-zinc-800 bg-zinc-900 px-3 py-2 text-center text-xl font-bold hover:border-emerald-500 active:bg-emerald-900"
            >
              <span className="text-xs text-zinc-500">#{s.ord}</span>
              <br />
              {s.valueNumeric}
              {unsentKeys?.has(s.clientIdempotencyKey) && (
                <>
                  <span
                    aria-hidden
                    className="absolute right-1 top-1 h-2 w-2 rounded-full bg-amber-400"
                  />
                  <span className="sr-only"> noch nicht gesendet</span>
                </>
              )}
            </button>
          </li>
        ))}
        {pendingBleShots.map((bleShot) => (
          <li key={`pending-${bleShot.ord}`}>
            <div
              className="block w-full rounded border-2 border-dashed border-amber-500/60 bg-amber-950/30 px-3 py-2 text-center text-xl font-bold text-amber-200"
              title="Vom Timer erkannt — Wertung oben wählen"
            >
              <span className="text-xs text-amber-400/80">#{bleShot.ord}</span>
              <br />
              <span className="text-base tabular-nums">{formatMs(bleShot.tFromStartMs)}</span>
            </div>
          </li>
        ))}
      </ul>

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 sm:items-center"
          onClick={() => !busy && setEditing(null)}
        >
          <div
            className="w-full max-w-md rounded-t-2xl bg-zinc-900 p-4 sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="mb-2 text-lg font-bold">Schuss #{editing.ord} korrigieren</h3>
            <p className="mb-3 text-xs text-zinc-500">
              Bisheriger Wert: <span className="font-bold">{editing.valueNumeric}</span>
            </p>
            <div className={`mb-3 grid ${cols} gap-2`}>
              {values.map((v) => (
                <button
                  key={v}
                  onClick={() => void applyEdit(v)}
                  disabled={busy}
                  className={`aspect-square rounded-lg border-2 text-2xl font-bold tabular-nums hover:border-emerald-500 disabled:opacity-30 ${
                    v === editing.valueNumeric
                      ? 'border-emerald-500 bg-emerald-900/30'
                      : 'border-zinc-700'
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
            <div className="flex justify-between">
              <button
                onClick={() => void applyDelete()}
                disabled={busy}
                className="min-h-12 rounded border border-red-700 px-4 py-2 text-sm text-red-300 hover:bg-red-900/40 disabled:opacity-30"
              >
                Löschen
              </button>
              <button
                onClick={() => setEditing(null)}
                disabled={busy}
                className="min-h-12 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-300 disabled:opacity-30"
              >
                Schließen
              </button>
            </div>
          </div>
        </div>
      )}
      <ShotUndoToast
        shot={deleted}
        shotCount={shotCount}
        onDone={() => setDeleted(null)}
        onRestored={onShot}
      />
    </section>
  );
}
